/**
 * `/privacy` — the privacy policy, and a Google OAuth prerequisite.
 *
 * Google requires a reachable privacy policy on an authorized domain before an External OAuth
 * app may leave "Testing" and be used by anyone with a Google account. Until this page existed,
 * sign-in was restricted to the handful of addresses listed as test users.
 *
 * WRITTEN FROM THE SCHEMA, NOT FROM A TEMPLATE. Every claim below is checked against
 * `apps/server/src/db/schema` — users, sessions, user_images, match_results, player_stats,
 * user_bans — so this describes what the code actually stores rather than what a generic policy
 * would assume. If a column is added that holds something about a person, this page is part of
 * that change.
 *
 * Two things it is careful to say accurately:
 * - The country flag is derived per connection and is NEVER written to the database (it lives on
 *   `PlayerState`, in memory, for the length of a match).
 * - Uploaded images go straight to Cloudflare R2 from the browser; the server stores only an
 *   index row and never handles the bytes.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from '@/lib/contact';

export const Route = createFileRoute('/privacy')({
  component: PrivacyPage,
});

/** Last substantive review of this text. Bump it when the practices below change. */
const LAST_UPDATED = '29 August 2026';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm text-muted-foreground">{children}</CardContent>
    </Card>
  );
}

function PrivacyPage() {
  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-10">
      <h1 className="text-3xl font-black tracking-tight">Privacy Policy</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Chompdome is in beta. Last updated {LAST_UPDATED}.
      </p>

      <Section title="What we collect">
        <p>
          <strong className="text-foreground">If you play as a guest</strong> — a display name and a
          device identifier your browser generates. No email, no password.
        </p>
        <p>
          <strong className="text-foreground">If you create an account</strong> — your email
          address, your display name, and a hash of your password. We never store the password
          itself.
        </p>
        <p>
          <strong className="text-foreground">If you sign in with Google</strong> — your email
          address, your name, and your Google account identifier. We request only the{' '}
          <code>openid</code>, <code>email</code> and <code>profile</code> scopes. We cannot read
          your Gmail, your Drive, your contacts or anything else in your Google account.
        </p>
        <p>
          <strong className="text-foreground">If you upload a picture</strong> — the image itself,
          its dimensions, size and type. Images are uploaded straight from your browser to
          Cloudflare R2 storage; our server never receives the file.
        </p>
        <p>
          <strong className="text-foreground">While you play</strong> — the results of each match:
          mass reached, pellets eaten, kills, time survived, and when it ended.
        </p>
      </Section>

      <Section title="What we do not collect">
        <p>
          We do not use advertising trackers or third-party analytics, and we do not sell or share
          your data with anyone for marketing.
        </p>
        <p>
          Your approximate country is worked out from your connection so the game can show a flag
          beside your name. It is used for the length of a match and{' '}
          <strong className="text-foreground">is never written to our database</strong>.
        </p>
      </Section>

      <Section title="Why we hold it">
        <p>
          To keep you signed in, to show leaderboards and personal bests, to let other players see
          the name and picture you chose, and to enforce bans when somebody spoils the game for
          other people. That is the whole list.
        </p>
      </Section>

      <Section title="Cookies">
        <p>
          One session cookie, so you stay signed in. It is <code>httpOnly</code> and{' '}
          <code>SameSite=Lax</code>, and it is not used to track you across other sites. The Android
          app stores the same session identifier in the device keystore instead of a cookie.
        </p>
      </Section>

      <Section title="Your choices">
        <p>
          You can change your display name and remove any picture you uploaded from your{' '}
          <Link to="/profile" className="font-medium text-primary hover:underline">
            profile
          </Link>{' '}
          page.
        </p>
        <p>
          To have your account and its data deleted, email{' '}
          <a href={SUPPORT_MAILTO} className="font-medium text-primary hover:underline">
            {SUPPORT_EMAIL}
          </a>{' '}
          from the address on the account and we will remove it.
        </p>
      </Section>

      <Section title="Children">
        <p>
          Chompdome is not directed at children under 13, and we do not knowingly collect their
          data.
        </p>
      </Section>

      <Section title="Changes and contact">
        <p>
          This is a beta and this policy may change. Questions go to{' '}
          <a href={SUPPORT_MAILTO} className="font-medium text-primary hover:underline">
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
      </Section>

      <p className="mt-6 text-center text-sm">
        <Link to="/" className="text-muted-foreground hover:underline">
          Back to Chompdome
        </Link>
      </p>
    </div>
  );
}
